import React, { createContext, useContext, useEffect, useState } from "react";
import { Droplet, Plus, X, Check } from "lucide-react";
import api from "./api";
import { useCart } from "./cart";
import { toast } from "sonner";

// SKUs that count as Bacteriostatic Water variants. Any of them in the
// basket means the customer already has reconstitution diluent — so we
// don't pop the prompt again.
export const BACT_SKUS = ["PL-BACT-3", "PL-BACT-10"];

const BactPromptContext = createContext(null);

export function BactPromptProvider({ children }) {
  const { items, addItem } = useCart();
  const [pending, setPending] = useState(null); // { product, qty }
  const [bacts, setBacts] = useState([]); // [PL-BACT-3 product, PL-BACT-10 product]

  useEffect(() => {
    Promise.all(BACT_SKUS.map((s) => api.get(`/products/${s}`).then((r) => r.data).catch(() => null)))
      .then((rows) => setBacts(rows.filter(Boolean)));
  }, []);

  // Lock body scroll when modal open
  useEffect(() => {
    if (!pending) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [pending]);

  const promptAndAdd = (product, qty = 1) => {
    const isReagent = product.category === "Laboratory Reagents" || BACT_SKUS.includes(product.sku);
    const alreadyHasBact = items.some((i) => BACT_SKUS.includes(i.sku));
    // No in-stock Bac Water to offer → the upsell modal would never render,
    // so add the peptide directly instead of silently doing nothing.
    const noBactToOffer = bacts.filter((b) => (b.stock_quantity || 0) > 0).length === 0;
    if (isReagent || alreadyHasBact || noBactToOffer) {
      addItem(product, qty);
      toast.success(`${product.name} ${product.strength} added to basket`, { duration: 1800 });
      return;
    }
    setPending({ product, qty });
  };

  const close = () => setPending(null);

  const onlyPeptide = () => {
    if (pending) {
      addItem(pending.product, pending.qty);
      toast.success(`${pending.product.name} ${pending.product.strength} added to basket`, { duration: 1800 });
    }
    close();
  };

  // Add the peptide + a specific Bac Water variant at its bundle price.
  const bothWith = (bact) => {
    if (pending && bact) {
      addItem(pending.product, pending.qty);
      // Untouched product — the cart applies the reagent bundle price itself.
      addItem(bact, 1);
      toast.success(`${pending.product.name} + Bac Water ${bact.strength} added to basket`, { duration: 1800 });
    }
    close();
  };

  // Only render the modal if we have at least one in-stock Bac variant.
  const inStockBacts = bacts.filter((b) => (b.stock_quantity || 0) > 0);

  return (
    <BactPromptContext.Provider value={{ promptAndAdd }}>
      {children}
      {pending && inStockBacts.length > 0 && (
        <BactPromptModal
          product={pending.product}
          qty={pending.qty}
          bacts={inStockBacts}
          onSkip={onlyPeptide}
          onAddBoth={bothWith}
          onClose={close}
        />
      )}
    </BactPromptContext.Provider>
  );
}

function BactPromptModal({ product, qty, bacts, onSkip, onAddBoth, onClose }) {
  const peptideTotal = product.price * qty;
  // Compute best-value (lowest £/ml at bundle price) for the badge.
  const byPerMl = (b) => (b.bundle_price || b.price) / parseFloat(b.strength);
  const bestSku = [...bacts].sort((a, b) => byPerMl(a) - byPerMl(b))[0]?.sku;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-y-auto"
      style={{ background: "rgba(6, 12, 22, 0.85)", backdropFilter: "blur(8px)" }}
      data-testid="bact-prompt-modal"
      onClick={onClose}
    >
      <div
        className="pg-card max-w-2xl w-full relative pg-fade-up max-h-[92dvh] sm:max-h-[90dvh] flex flex-col rounded-t-sm sm:rounded-sm overflow-hidden"
        style={{ borderColor: "rgba(166, 255, 0, 0.35)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-20 text-[#9CA8B8] hover:text-[#F7FAFC]"
          aria-label="Close"
          data-testid="bact-prompt-close"
        >
          <X size={20} />
        </button>

        <div className="absolute top-3 left-4 sm:-top-3 sm:left-8 z-10 px-3 py-1 bg-[#0B1220] border border-[#A6FF00] rounded-sm">
          <span className="pg-mono text-[10px] uppercase tracking-[0.22em] text-[#A6FF00]">
            Add Bacteriostatic Water?
          </span>
        </div>

        <div className="p-5 pt-12 sm:p-8 sm:pt-8 overflow-y-auto overscroll-contain">
          <div className="pg-micro mb-3">// Recommended add-on for reconstitution work</div>
          <h2
            className="text-[#F7FAFC] text-xl sm:text-2xl pg-display mb-2 leading-tight"
            style={{ fontFamily: "'Exo 2', sans-serif" }}
          >
            Want a vial of Bacteriostatic Water?
          </h2>
          <p className="text-[#9CA8B8] text-sm leading-relaxed mb-5 sm:mb-6">
            Most researchers ordering {product.name} also pick up sterile
            bacteriostatic water for reconstitution work. Add a vial now at the
            bundle price — pick the size that suits your workflow.
          </p>

          {/* Peptide line */}
          <div className="p-3 sm:p-4 border border-[#00C2D6]/25 rounded-sm flex items-center gap-3 mb-4">
            <div className="w-11 h-11 sm:w-12 sm:h-12 border border-[#00C2D6]/40 rounded-sm flex items-center justify-center pg-mono text-[10px] text-[#5EEAD4] tracking-[0.18em] shrink-0">
              {product.sku.split("-")[1]}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[#F7FAFC] pg-heading text-sm sm:text-base leading-tight" style={{ fontFamily: "'Rajdhani'" }}>
                {product.name} {product.strength}
              </div>
              <div className="text-xs text-[#9CA8B8] mt-0.5">
                Qty {qty} · £{peptideTotal.toFixed(2)}
              </div>
            </div>
          </div>

          {/* Bac variants — side by side */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5 sm:mb-6">
            {bacts.map((b) => {
              const bundle = b.bundle_price || b.price;
              const saving = Math.max(0, (b.price || 0) - bundle);
              const bothTotal = peptideTotal + bundle;
              const isBest = b.sku === bestSku && bacts.length > 1;
              return (
                <button
                  key={b.sku}
                  onClick={() => onAddBoth(b)}
                  className="relative p-4 border rounded-sm text-left flex flex-col gap-1 hover:border-[#A6FF00] transition-colors"
                  style={{
                    borderColor: isBest ? "rgba(166,255,0,0.55)" : "rgba(94,234,212,0.5)",
                    background: isBest ? "rgba(166,255,0,0.04)" : "rgba(94,234,212,0.04)",
                  }}
                  data-testid={`bact-prompt-add-${b.sku}`}
                >
                  {isBest && (
                    <span
                      className="absolute -top-2 left-3 px-2 py-0.5 text-[9px] tracking-[0.22em] uppercase pg-mono font-bold"
                      style={{ background: "#0B1220", color: "#A6FF00", border: "1px solid #A6FF00", borderRadius: 2 }}
                    >
                      Best value
                    </span>
                  )}
                  <div className="flex items-center gap-2">
                    <Droplet size={16} className="text-[#5EEAD4]" />
                    <span className="text-[#F7FAFC] pg-heading text-base" style={{ fontFamily: "'Rajdhani'" }}>
                      Bac Water · {b.strength}
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className="text-[#A6FF00] pg-display text-lg" style={{ fontFamily: "'Exo 2'" }}>
                      £{bundle.toFixed(2)}
                    </span>
                    {saving > 0 && (
                      <span className="text-[#9CA8B8] text-[11px] line-through">£{b.price.toFixed(2)}</span>
                    )}
                    {saving > 0 && (
                      <span className="pg-mono text-[10px] tracking-[0.12em] text-[#5EEAD4]">save £{saving.toFixed(2)}</span>
                    )}
                  </div>
                  <div className="mt-2 text-xs text-[#9CA8B8]">
                    Add both — <strong className="text-[#F7FAFC]">£{bothTotal.toFixed(2)}</strong>
                  </div>
                  <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] pg-mono uppercase tracking-[0.18em] text-[#A6FF00]">
                    <Plus size={12} /> Add this size
                  </div>
                </button>
              );
            })}
          </div>

          <button
            onClick={onSkip}
            className="pg-btn-ghost w-full sm:w-auto"
            data-testid="bact-prompt-skip"
          >
            <Check size={14} /> Just the peptide
          </button>

          <p className="mt-4 text-[10px] pg-mono uppercase tracking-[0.18em] text-[#9CA8B8] text-center">
            Sterile · Same-day UK dispatch · Research use only
          </p>
        </div>
      </div>
    </div>
  );
}

export const useBactPrompt = () => useContext(BactPromptContext);
